import TableTemplate from '../../html/components/table.html'
import TableHeadTemplate from '../../html/components/table-head.html'
import TableBodyTemplate from '../../html/components/table-body.html'
import TableItemTemplate from '../../html/components/table-item.html'
import TableCellTemplate from '../../html/components/table-cell.html'

import Component from '../shared/component'
import Relation, {HasMany, HasOne, MemberOf} from '../shared/mixins/relation'

import {InteractiveMixin} from './interactive'

const tableHead = Relation()
const tableBody = Relation()
const tableItems = Relation()
const tableCells = Relation()
const tableGroups = Relation()

export const Table = Component('v-table', {
  mixins: [
    HasOne(tableHead, 'head'),
    HasOne(tableBody, 'body'),
    HasMany(tableGroups, 'groups')
  ],
  template: TableTemplate,
  computed: {
    items () {
      const {head, body} = this

      let items = []

      if (head) {
        items = items.concat([head.item])
      }

      if (body) {
        items = items.concat(body.items)
      }

      return items
    },

    rows () {
      return this.items.map(item => {
        return {
          cells: item.cells
        }
      })
    },

    columns () {
      return this.items.reduce((columns, item, row) => {
        item.cells.forEach((cell, column) => {
          if (!columns[column]) {
            columns[column] = {cells: []}
          }

          columns[column].cells[row] = cell
        })

        return columns
      }, [])
    }
  },
  watch: {
    items () {
      const {groups, items} = this

      items.forEach((item, row) => {
        item.cells.forEach((cell, column) => {
          cell.row = row
          cell.column = column
          cell.group = groups[column] || null
        })
      })
    }
  }
})

export const TableHead = Component('v-table-head', {
  mixins: [
    MemberOf(tableHead, 'table'),
    HasOne(tableItems, 'item')
  ],
  template: TableHeadTemplate,
  computed: {
    type () {
      return 'head'
    }
  }
})

export const TableBody = Component('v-table-body', {
  mixins: [
    MemberOf(tableBody, 'table'),
    HasMany(tableItems, 'items')
  ],
  template: TableBodyTemplate,
  computed: {
    type () {
      return 'body'
    }
  }
})

export const TableItem = Component('v-table-item', {
  mixins: [
    MemberOf(tableItems, 'owner'),
    HasMany(tableCells, 'cells')
  ],
  template: TableItemTemplate,
  props: {
    selected: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    classes () {
      return {
        'is-selected': this.selected
      }
    },

    inHead () {
      return this.owner && this.owner.type === 'head'
    },

    inBody () {
      return this.owner && this.owner.type === 'body'
    }
  }
})

export const TableCell = Component('v-table-cell', {
  mixins: [
    MemberOf(tableCells, 'item'),
    InteractiveMixin
  ],
  template: TableCellTemplate,
  data () {
    return {
      row: -1,
      column: -1,
      group: null
    }
  },
  computed: {
    classes () {
      return {
        'has-interactive': this.interactive || this.sortable
      }
    },

    inHead () {
      return this.item && this.item.inHead
    },

    inBody () {
      return this.item && this.item.inBody
    },

    sortable () {
      const {group} = this

      if (group) {
        return group.sortable && this.inHead
      }
    },

    style () {
      const {group} = this

      if (group) {
        return group.style
      }
    }
  }
})

export const TableGroup = Component('v-table-group', {
  abstract: true,
  mixins: [
    MemberOf(tableGroups, 'table')
  ],
  props: {
    size: {
      type: [
        String,
        Number
      ],
      default: null
    },
    grow: {
      type: [
        String,
        Number
      ],
      default: 0
    },
    shrink: {
      type: [
        String,
        Number
      ],
      default: 0
    },
    sortable: {
      type: Boolean,
      default: false
    },
    order: {
      type: String,
      default: null,
      valid: [
        'ascending',
        'descending'
      ]
    }
  },
  data () {
    return {
      active: null
    }
  },
  computed: {
    basis () {
      let {size} = this

      if (size === null) {
        return '0%'
      }

      let unit = 'px'

      if (size.match(/%/)) {
        unit = '%'
      }

      switch (size.constructor) {
        case String:
          size = parseFloat(size)
          break
        case Number:
      }

      if (size <= 1) {
        size *= 100
        unit = '%'
      }

      return `${size}${unit}`
    },

    style () {
      const {basis, size, grow, shrink} = this

      if (size !== null || grow !== 0 || shrink !== 0) {
        return {
          flex: `${grow} ${shrink} ${basis}`
        }
      }
    }
  },
  methods: {
    sort () {
      switch (true) {
        case this.active === null:
        case this.active === 'descending':
          this.active = 'ascending'
          break
        case this.active === 'ascending':
          this.active = 'descending'
      }

      this.$emit('sort', this.active)
    }
  }
})
